import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
    Plane, Clock, ArrowRight, Loader2, Users, Shield, ShieldCheck,
    Package, CheckCircle, UserCheck, Fuel, Activity, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getLocalToday } from '../../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
    scheduled: 'Programado', confirmed: 'Confirmado', preparing: 'Preparando',
    boarding: 'Abordando', closed: 'Cerrado', departed: 'Despegado',
    en_route: 'En Vuelo', arrived: 'Llegado', delayed: 'Demorado',
};
const statusColors = {
    scheduled: 'bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-500/20',
    confirmed: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    preparing: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    boarding: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
    closed: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    departed: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
    en_route: 'bg-green-500/10 text-green-400 border-green-500/20',
    arrived: 'bg-emerald-600/10 text-emerald-500 border-emerald-600/20',
    delayed: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
};

function ReadinessBar({ value, label }) {
    const color = value >= 80 ? 'bg-emerald-500' : value >= 50 ? 'bg-amber-500' : 'bg-red-500';
    return (
        <div className="space-y-0.5">
            <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</span>
                <span className="text-[10px] font-mono font-bold">{value}%</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${value}%` }} />
            </div>
        </div>
    );
}

function StageIndicator({ done, label, icon: Icon }) {
    return (
        <div className={`flex flex-col items-center gap-0.5 ${done ? 'text-emerald-500' : 'text-muted-foreground/40'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${done ? 'bg-emerald-500/20' : 'bg-muted/50'}`}>
                <Icon className="w-3.5 h-3.5" />
            </div>
            <span className="text-[9px] font-medium">{label}</span>
        </div>
    );
}

export function ReadinessBoard() {
    const { token } = useAuth();
    const headers = { Authorization: `Bearer ${token}` };
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateFilter, setDateFilter] = useState(getLocalToday());

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/turnaround/dashboard?date=${dateFilter}`, { headers });
            setData(res.data);
        } catch { toast.error('Error al cargar datos'); }
        finally { setLoading(false); }
    }, [dateFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Auto-refresh every 30 seconds
    useEffect(() => {
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, [fetchData]);

    return (
        <div className="space-y-6" data-testid="readiness-board">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-sm text-muted-foreground">Preparación del vuelo: aeronave, tripulación, pasajeros, despacho y rampa</p>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        Auto-refresh 30s
                    </div>
                    <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="w-[160px] input-aviation font-mono" data-testid="turnaround-date" />
                </div>
            </div>

            {/* Summary */}
            {data?.summary && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <Card className="card-aviation">
                        <CardContent className="p-3 flex items-center gap-2">
                            <Plane className="w-5 h-5 text-primary" />
                            <div><p className="text-xl font-bold font-mono">{data.summary.total_flights}</p><p className="text-[10px] text-muted-foreground uppercase">Total</p></div>
                        </CardContent>
                    </Card>
                    <Card className="card-aviation">
                        <CardContent className="p-3 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                            <div><p className="text-xl font-bold font-mono">{data.summary.fully_ready}</p><p className="text-[10px] text-muted-foreground uppercase">100% Listos</p></div>
                        </CardContent>
                    </Card>
                    <Card className="card-aviation">
                        <CardContent className="p-3 flex items-center gap-2">
                            <Activity className="w-5 h-5 text-blue-400" />
                            <div><p className="text-xl font-bold font-mono">{data.summary.avg_readiness}%</p><p className="text-[10px] text-muted-foreground uppercase">Promedio</p></div>
                        </CardContent>
                    </Card>
                    <Card className="card-aviation">
                        <CardContent className="p-3 flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-emerald-500" />
                            <div><p className="text-xl font-bold font-mono">{data.summary.dispatched}</p><p className="text-[10px] text-muted-foreground uppercase">Despachados</p></div>
                        </CardContent>
                    </Card>
                    <Card className="card-aviation">
                        <CardContent className="p-3 flex items-center gap-2">
                            <Package className="w-5 h-5 text-amber-500" />
                            <div><p className="text-xl font-bold font-mono">{data.summary.ramp_completed}</p><p className="text-[10px] text-muted-foreground uppercase">Rampa OK</p></div>
                        </CardContent>
                    </Card>
                    {(data.summary.alerts_count || 0) > 0 && (
                        <Card className="card-aviation border-red-500/30">
                            <CardContent className="p-3 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                                <div><p className="text-xl font-bold font-mono text-red-500">{data.summary.alerts_count}</p><p className="text-[10px] text-muted-foreground uppercase">Alertas</p></div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Alerts Banner */}
            {data?.alerts?.length > 0 && (
                <div className="space-y-2" data-testid="turnaround-alerts">
                    {data.alerts.map(alert => (
                        <div key={alert.flight_id} className={`flex items-center gap-3 p-3 rounded-lg border ${
                            alert.severity === 'critical' ? 'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-400' :
                            alert.severity === 'warning' ? 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400' :
                            'bg-blue-500/10 border-blue-500/30 text-blue-400'
                        }`} data-testid={`alert-${alert.flight_id}`}>
                            <AlertTriangle className="w-5 h-5 shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm font-bold">
                                    {alert.flight_number} ({alert.origin}→{alert.destination}) — {alert.minutes_until_departure} min para salida
                                </p>
                                <p className="text-xs opacity-80">
                                    Readiness: {alert.readiness}% | Falta: {alert.missing.join(', ') || 'PAX'}
                                </p>
                            </div>
                            <Badge className={`${alert.severity === 'critical' ? 'bg-red-500/20 text-red-700 dark:text-red-400' : 'bg-amber-500/20 text-amber-700 dark:text-amber-400'} text-[10px]`}>
                                {alert.severity === 'critical' ? 'CRITICO' : 'ALERTA'}
                            </Badge>
                        </div>
                    ))}
                </div>
            )}

            {/* Flight cards */}
            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : !data?.flights?.length ? (
                <Card className="card-aviation"><CardContent className="p-12 text-center text-muted-foreground"><Plane className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No hay vuelos para esta fecha</p></CardContent></Card>
            ) : (
                <div className="space-y-3" data-testid="turnaround-flights">
                    {data.flights.map(f => (
                        <Card key={f.flight_id} className="card-aviation" data-testid={`turnaround-${f.flight_id}`}>
                            <CardContent className="p-4">
                                <div className="flex items-start gap-4">
                                    {/* Flight info */}
                                    <div className="min-w-[130px]">
                                        <p className="font-bold font-mono text-lg">{f.flight_number}</p>
                                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                            <span className="font-mono">{f.origin}</span>
                                            <ArrowRight className="w-3 h-3" />
                                            <span className="font-mono">{f.destination}</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                                            <Clock className="w-3 h-3" />
                                            <span className="font-mono">{f.scheduled_departure?.split('T')[1]?.slice(0,5)}</span>
                                        </div>
                                        <Badge className={`mt-1 text-[10px] ${statusColors[f.status] || ''}`}>{statusLabels[f.status] || f.status}</Badge>
                                    </div>

                                    {/* Stages */}
                                    <div className="flex items-center gap-1.5">
                                        <StageIndicator done={f.has_aircraft} label="A/C" icon={Plane} />
                                        <div className={`w-4 h-0.5 ${f.has_aircraft && f.has_crew ? 'bg-emerald-500/50' : 'bg-muted'}`} />
                                        <StageIndicator done={f.has_crew} label="Crew" icon={Users} />
                                        <div className={`w-4 h-0.5 ${f.has_crew && f.pax_total > 0 ? 'bg-emerald-500/50' : 'bg-muted'}`} />
                                        <StageIndicator done={f.pax_total > 0} label="PAX" icon={UserCheck} />
                                        <div className={`w-4 h-0.5 ${f.pax_total > 0 && f.dispatch_released ? 'bg-emerald-500/50' : 'bg-muted'}`} />
                                        <StageIndicator done={f.dispatch_released} label="Desp." icon={Shield} />
                                        <div className={`w-4 h-0.5 ${f.dispatch_released && f.ramp_completed ? 'bg-emerald-500/50' : 'bg-muted'}`} />
                                        <StageIndicator done={f.ramp_completed} label="Rampa" icon={Package} />
                                    </div>

                                    {/* Progress bars */}
                                    <div className="flex-1 space-y-1.5 min-w-[200px]">
                                        <ReadinessBar value={f.pax_progress} label="Check-in" />
                                        <ReadinessBar value={f.ramp_progress} label="Rampa" />
                                        <ReadinessBar value={f.readiness} label="Readiness" />
                                    </div>

                                    {/* Quick stats */}
                                    <div className="text-right text-xs space-y-1 min-w-[100px]">
                                        {f.aircraft_registration && <p className="font-mono font-bold">{f.aircraft_registration}</p>}
                                        <p className="text-muted-foreground">{f.crew_count} crew</p>
                                        <p className="text-muted-foreground">{f.pax_checked_in}/{f.pax_total} PAX</p>
                                        <div className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${f.readiness === 100 ? 'bg-emerald-500/10 text-emerald-500' : f.readiness >= 50 ? 'bg-amber-500/10 text-amber-500' : 'bg-red-500/10 text-red-500'}`}>
                                            {f.readiness}% LISTO
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
